import client from './client';
import { qsStringify } from '../shared/utils/QueryString';
import {
  PaginationRequest,
  PaginationResponse,
  Virtual,
  VirtualContributionStats,
  VirtualQuery,
  VirtualCoreContributorStat,
} from '../interfaces';
import { Contribution } from './contribution.api';

const getVirtuals = async (query: VirtualQuery) => {
  let queryString = qsStringify({
    filters: {
      name: {
        $contains: query.name,
      },
      status: query.status,
      hasPlayground: query.hasPlayground,
      category: {
        $ne: query.displayBase ? null : 'BASE',
      },
      id: {
        $in: query.id,
      },
    },
    sort: query.sortBy,
    populate: ['image', 'cores'],
    pagination: {
      page: query.page,
      pageSize: query.pageSize,
    },
  });

  const url = `/api/virtuals?${queryString}`;
  const res = await client.get<PaginationResponse<Virtual[]>>(url);

  return res.data;
};

const getBaseVirtuals = async (query: VirtualQuery) => {
  const queryString = qsStringify({
    filters: {
      name: {
        $contains: query.name,
      },
      cores: {
        name: {
          $eq: query.core,
        },
      },
      status: query.status,
      category: 'BASE',
    },
    sort: 'updatedAt:desc',
    populate: ['image', 'cores'],
    pagination: {
      page: query.page,
    },
  });

  const url = `/api/virtuals?${queryString}`;
  const res = await client.get<PaginationResponse<Virtual[]>>(url);

  return res.data;
};

const getVirtual = async (virtualId: number): Promise<Virtual> => {
  const queryString = qsStringify({
    populate: ['cores', 'image', 'contributions', 'contributions.user'],
  });

  const res = await client.get<PaginationResponse<Virtual>>(
    `/api/virtuals/${virtualId}?${queryString}`
  );

  return res.data.data;
};

const getVirtualContributions = async (
  virtualId: number,
  coreId: number,
  filters?: any
): Promise<Virtual> => {
  const queryString = qsStringify({
    populate: [
      'cores',
      'contributions',
      'contributions.user',
      'contributions.baseModel',
      'contributions.baseDataset',
      'contributions.commit',
      'contributions.core',
    ],
    filters,
    sort: 'contributions.createdAt:desc',
  });

  const res = await client.get<PaginationResponse<Virtual>>(
    `/api/virtuals/${virtualId}?${queryString}`
  );

  return res.data.data;
};

const getVirtualsOnVotingPhase = async (): Promise<Virtual[]> => {
  const queryString = qsStringify({
    filters: {
      status: 'VOTING',
    },
  });

  const res = await client.get<PaginationResponse<Virtual[]>>(
    `/api/virtuals?${queryString}`
  );

  return res.data.data;
};

const createVirtual = async (data: any) => {
  const res = await client.post<any>(`/api/virtuals`, data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return await getVirtual(res.data.data.id);
};

const updateVirtual = async (virtualId: number, data: object) => {
  const res = await client.put<any>(`/api/virtuals/${virtualId}`, {
    data: data,
  });

  return res;
};

const getMyVirtuals = async (query: PaginationRequest) => {
  const queryString = qsStringify({
    pagination: {
      page: query.page,
    },
  });

  const res = await client.get<PaginationResponse<Virtual[]>>(
    `/api/virtuals/me?${queryString}`
  );

  return res.data;
};

const getVirtualContributionStats = async (virtualId: number) => {
  let url = `/api/virtuals/${virtualId}/contributionStats`;

  const res = await client.get<{
    data: VirtualContributionStats;
  }>(url);

  return res.data;
};

const getVirtualContributionByCoreId = async (
  virtualId: number,
  coreId: number,
  version?: number
) => {
  const query = qsStringify({
    coreId,
    version,
    status: {
      $notIn: ['DRAFT', 'PROCESSING', 'ORCHESTRATING'],
    },
  });

  const res = await client.get<{
    data: Contribution;
  }>(`/api/virtuals/${virtualId}/contributions?${query}`);

  return res.data;
};

const getVirtualCoreContributorsStats = async (
  virtualId: number,
  coreId: number
) => {
  const res = await client.get<{ data: VirtualCoreContributorStat[] }>(
    `/api/virtuals/${virtualId}/cores/${coreId}/contributors-stats`
  );

  return res.data;
};

export {
  getVirtuals,
  getVirtual,
  createVirtual,
  updateVirtual,
  getMyVirtuals,
  getVirtualContributionStats,
  getVirtualContributionByCoreId,
  getBaseVirtuals,
  getVirtualsOnVotingPhase,
  getVirtualContributions,
  getVirtualCoreContributorsStats,
};
